import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
  faShieldAlt,
  faSearch,
  faFilter,
  faChevronLeft,
  faChevronRight,
  faEye,
  faTimes,
  faChartBar,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import "../css/Common.css";
import "./css/AdminScamReports.css";
import AdminScamReportAPI from "../services/AdminScamReportAPI";
import { useTranslation } from "../../../i18n/LanguageContext";
import LoadingSpinner from "../common/LoadingSpinner";
import RefreshButton from "../common/RefreshButton";
import { formatDateTime } from "../utils/DateTimeUtil";
import { decodeAndSanitizeHtml } from "../scamdetector/text/textAnalysisUtils";

const ANALYSIS_TYPES = [
  { value: "", label: "All Types" },
  { value: "URL_SAFETY", label: "URL Safety" },
  { value: "SMS_ANALYSIS", label: "SMS Analysis" },
  { value: "TEXT_ANALYSIS", label: "Text Analysis" },
  { value: "EMAIL_ANALYSIS", label: "Email Analysis" },
  { value: "IMAGE_ANALYSIS", label: "Image Analysis" },
  { value: "SOCIAL_MEDIA_ANALYSIS", label: "Social Media" },
  { value: "PHONE_NUMBER_CHECK", label: "Phone Number" },
];

const RISK_LEVELS = [
  { value: "", label: "All Risk Levels" },
  { value: "CRITICAL", label: "Critical" },
  { value: "HIGH", label: "High" },
  { value: "MEDIUM", label: "Medium" },
  { value: "LOW", label: "Low" },
];

const DATE_RANGES = [
  { value: "", label: "All Time" },
  { value: "7", label: "Last 7 Days" },
  { value: "30", label: "Last 30 Days" },
  { value: "90", label: "Last 90 Days" },
];

/**
 * Platform Admin page for viewing all scam reports across the platform.
 * Accessible via Platform Admin > Scam Reports in the sidebar.
 */
function AdminScamReports() {
  const { t } = useTranslation();
  // Data
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [pageInput, setPageInput] = useState("");

  // Filters
  const [analysisType, setAnalysisType] = useState("");
  const [riskLevel, setRiskLevel] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [uuidSearch, setUuidSearch] = useState("");
  const [uuidInput, setUuidInput] = useState("");

  // Detail panel
  const [selectedReport, setSelectedReport] = useState(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // DATA LOADING
  // ═══════════════════════════════════════════════════════════════════════════

  const loadReports = useCallback(
    async (page = currentPage) => {
      setLoading(true);
      try {
        const response = await AdminScamReportAPI.getAllReports(
          page,
          pageSize,
          {
            analysisType: analysisType || undefined,
            riskLevel: riskLevel || undefined,
            days: dateRange || undefined,
            userUuid: uuidSearch || undefined,
          },
        );
        setReports(response.reports || []);
        setTotalPages(response.totalPages || 0);
        setTotalElements(response.totalElements || 0);
        setCurrentPage(response.currentPage || 0);
      } catch (err) {
        toast.error(t("adminScamReports.toast.loadFailed"));
        console.error("Load error:", err);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageSize, analysisType, riskLevel, dateRange, uuidSearch],
  );

  useEffect(() => {
    loadReports(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisType, riskLevel, dateRange, uuidSearch, pageSize]);

  const handleRefresh = () => {
    loadReports(currentPage);
  };

  const handleUuidSearch = () => {
    setUuidSearch(uuidInput.trim());
  };

  const handleUuidKeyDown = (e) => {
    if (e.key === "Enter") handleUuidSearch();
  };

  const clearUuidSearch = () => {
    setUuidInput("");
    setUuidSearch("");
  };

  const handleDeleteReport = async (reportId) => {
    if (
      !window.confirm(`Are you sure you want to delete Report #${reportId}?`)
    ) {
      return;
    }
    try {
      await AdminScamReportAPI.deleteReport(reportId);
      toast.success(
        t("adminScamReports.toast.deleted").replace("{id}", reportId),
      );
      if (selectedReport?.id === reportId) setSelectedReport(null);
      loadReports(currentPage);
    } catch (err) {
      toast.error(t("adminScamReports.toast.deleteFailed"));
      console.error("Delete error:", err);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  const getRiskBadgeClass = (level) => {
    switch (level?.toUpperCase()) {
      case "CRITICAL":
        return "risk-badge risk-critical";
      case "HIGH":
        return "risk-badge risk-high";
      case "MEDIUM":
        return "risk-badge risk-medium";
      case "LOW":
        return "risk-badge risk-low";
      default:
        return "risk-badge risk-unknown";
    }
  };

  const getAnalysisTypeLabel = (type) => {
    const found = ANALYSIS_TYPES.find((t) => t.value === type);
    return found ? found.label : type || "Unknown";
  };

  const truncate = (text, maxLen = 80) => {
    if (!text) return "—";
    return text.length > maxLen ? text.substring(0, maxLen) + "…" : text;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="admin-scam-reports">
      {/* Compact Header + Filters Bar */}
      <div className="asr-toolbar">
        <div className="toolbar-left">
          <FontAwesomeIcon icon={faShieldAlt} className="toolbar-icon" />
          <h1>Scam Reports</h1>
          <span className="toolbar-count">
            {totalElements} report{totalElements !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="toolbar-right">
          <RefreshButton onClick={handleRefresh} loading={loading} />
        </div>
      </div>

      {/* Filter Row */}
      <div className="asr-filters">
        <div className="filter-group">
          <FontAwesomeIcon icon={faFilter} className="filter-icon" />
          <select
            value={analysisType}
            onChange={(e) => setAnalysisType(e.target.value)}
            className="asr-select"
          >
            {ANALYSIS_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <select
            value={riskLevel}
            onChange={(e) => setRiskLevel(e.target.value)}
            className="asr-select"
          >
            {RISK_LEVELS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="asr-select"
          >
            {DATE_RANGES.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group uuid-filter">
          <div className="uuid-input-wrap">
            <input
              type="text"
              className="asr-input"
              placeholder="Search by UUID..."
              value={uuidInput}
              onChange={(e) => setUuidInput(e.target.value)}
              onKeyDown={handleUuidKeyDown}
            />
            {uuidSearch && (
              <button
                className="uuid-clear-btn"
                onClick={clearUuidSearch}
                title="Clear"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          <button className="asr-search-btn" onClick={handleUuidSearch}>
            <FontAwesomeIcon icon={faSearch} />
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <LoadingSpinner message="Loading scam reports..." />
      ) : reports.length === 0 ? (
        <div className="asr-empty">
          <FontAwesomeIcon icon={faSearch} className="empty-icon" />
          <h3>No Reports Found</h3>
          <p>No scam reports match the current filters.</p>
        </div>
      ) : (
        <>
          <div className="asr-table-scroll">
            <table className="asr-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Risk</th>
                  <th>User UUID</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr
                    key={report.id}
                    className={`asr-clickable-row${selectedReport?.id === report.id ? " row-selected" : ""}`}
                    onClick={() => setSelectedReport(report)}
                  >
                    <td className="col-id">{report.id}</td>
                    <td className="col-date">
                      {formatDateTime(report.createdAt)}
                    </td>
                    <td className="col-type">
                      <span className="type-badge">
                        {getAnalysisTypeLabel(report.analysisType)}
                      </span>
                    </td>
                    <td className="col-title">
                      {truncate(report.analysisTitle || report.analysisDetail)}
                    </td>
                    <td className="col-risk">
                      <span className={getRiskBadgeClass(report.riskLevel)}>
                        {report.riskLevel || "—"}
                      </span>
                    </td>
                    <td className="col-uuid" title={report.userUuid}>
                      {report.userUuid
                        ? report.userUuid.substring(0, 8) + "…"
                        : "—"}
                    </td>
                    <td
                      className="col-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="asr-view-btn"
                        onClick={() => setSelectedReport(report)}
                        title="View details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        className="asr-delete-btn"
                        onClick={() => handleDeleteReport(report.id)}
                        title="Delete report"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pagination — always visible at bottom */}
      {!loading && reports.length > 0 && (
        <div className="asr-pagination">
          <div className="pagination-left">
            <span className="asr-page-range">
              {currentPage * pageSize + 1}–
              {Math.min((currentPage + 1) * pageSize, totalElements)} of{" "}
              {totalElements}
            </span>
            <select
              className="asr-page-size-select"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              title="Rows per page"
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
          </div>
          <div className="pagination-center">
            <button
              className="asr-page-btn"
              disabled={currentPage === 0}
              onClick={() => loadReports(0)}
              title="First page"
            >
              First
            </button>
            <button
              className="asr-page-btn"
              disabled={currentPage === 0}
              onClick={() => loadReports(currentPage - 1)}
            >
              <FontAwesomeIcon icon={faChevronLeft} /> Prev
            </button>
            <span className="asr-page-info">
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              className="asr-page-btn"
              disabled={currentPage >= totalPages - 1}
              onClick={() => loadReports(currentPage + 1)}
            >
              Next <FontAwesomeIcon icon={faChevronRight} />
            </button>
            <button
              className="asr-page-btn"
              disabled={currentPage >= totalPages - 1}
              onClick={() => loadReports(totalPages - 1)}
              title="Last page"
            >
              Last
            </button>
          </div>
          <div className="pagination-right">
            <label className="page-jump-label">Go to:</label>
            <input
              type="number"
              className="asr-page-jump"
              min={1}
              max={totalPages}
              placeholder="#"
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const p = parseInt(pageInput, 10);
                  if (p >= 1 && p <= totalPages) {
                    loadReports(p - 1);
                    setPageInput("");
                  }
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Detail Side Panel */}
      {selectedReport && (
        <ReportDetailPanel
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
          getAnalysisTypeLabel={getAnalysisTypeLabel}
          getRiskBadgeClass={getRiskBadgeClass}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DETAIL SIDE PANEL
// ═══════════════════════════════════════════════════════════════════════════════

function ReportDetailPanel({
  report,
  onClose,
  getAnalysisTypeLabel,
  getRiskBadgeClass,
}) {
  return (
    <div className="asr-detail-backdrop" onClick={onClose}>
      <div className="asr-detail-panel" onClick={(e) => e.stopPropagation()}>
        <div className="detail-header">
          <h2>
            <FontAwesomeIcon icon={faChartBar} /> Report #{report.id}
          </h2>
          <button className="detail-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="detail-body">
          {/* Analysis Content */}
          {report.analysisTitle && (
            <div className="detail-section">
              <h3>Title</h3>
              <p className="detail-text">{report.analysisTitle}</p>
            </div>
          )}

          {report.analysisDetail && (
            <div className="detail-section">
              <h3>Detail</h3>
              <p className="detail-text">{report.analysisDetail}</p>
            </div>
          )}

          {report.analysisInput && (
            <div className="detail-section">
              <h3>Input</h3>
              <pre className="detail-pre">{report.analysisInput}</pre>
            </div>
          )}

          {report.context && (
            <div className="detail-section">
              <h3>User Context</h3>
              <p className="detail-text">{report.context}</p>
            </div>
          )}

          {report.suggestions && (
            <div className="detail-section">
              <h3>What Should You Do?</h3>
              <div
                className="detail-text detail-html"
                dangerouslySetInnerHTML={{
                  __html: decodeAndSanitizeHtml(report.suggestions),
                }}
              />
            </div>
          )}

          {report.explanation && (
            <div className="detail-section">
              <h3>Explanation</h3>
              <div
                className="detail-text detail-html"
                dangerouslySetInnerHTML={{
                  __html: decodeAndSanitizeHtml(report.explanation),
                }}
              />
            </div>
          )}

          {report.riskFactors && (
            <div className="detail-section">
              <h3>Risk Factors</h3>
              <p className="detail-text">{report.riskFactors}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminScamReports;
